import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import Scroll from 'react-scroll';
import { shuffleArray } from 'shared';
import { StrapiAgenciesResponse } from 'types';
import expectations from '../assets/agencies/expectations.png';
import hero from '../assets/agencies/hero.jpg';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';

export async function getStaticProps() {
  const params = {
    populate: { logo: { fields: ['name', 'url', 'height', 'width'] } },
    pagination: { page: 1, pageSize: 100 },
  };

  const [data] = await strapi.get<StrapiAgenciesResponse>(`/api/agencies`, { params });
  const agencies = shuffleArray(data.data.map(p => p.attributes));

  return {
    props: { agencies },
    revalidate: 21600,
  };
}

export default function ReferralAgencies({ agencies }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main>
      <Meta title="referring agencies" description="Beds for Kids Referral Agencies" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Referring Agents"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">referring agencies</h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Thousands of children in our city do not have a bed to call their own. In order to serve such a large
          population in need, Beds for Kids partners with over 40 referral agencies across the Charlotte community. The
          social workers at each organization send referrals directly to Beds for Kids and we reach out to the client
          families to verify their needs and schedule the deliveries. We work hand in hand with our referral partners to
          provide stability and continue upward mobility for our client families through beds and essential furniture.
        </p>
        <div className="mx-auto grid max-w-xl gap-4 px-4 py-8 md:grid-cols-2">
          <div className="flex flex-col">
            <h4 className="mb-2 w-full text-center font-avenir font-semibold italic text-green-500">
              become a referring agency:
            </h4>
            <Button
              bType="secondary"
              className="w-full justify-center"
              onClick={() =>
                Scroll.scroller.scrollTo('agency-apply', {
                  duration: 500,
                  smooth: true,
                  offset: -100,
                })
              }>
              apply now
            </Button>
          </div>
          <div className="flex flex-col">
            <h4 className="mb-2 w-full text-center font-avenir font-semibold italic text-green-500">
              already have an account?
            </h4>
            <Button bType="secondary" className="w-full justify-center" link="/login">
              sign in
            </Button>
          </div>
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h2 className="mx-auto mb-4 max-w-3xl px-4 text-center font-avenir text-2xl font-semibold md:text-4xl">
          Beds For Kids Currently Works Through The Following Referral Agencies:
        </h2>
        <div className="mx-auto grid max-w-5xl grid-cols-3 items-center justify-center gap-x-12 px-4 md:grid-cols-6">
          {agencies.map(a => (
            <div className="flex aspect-1 items-center justify-center" key={`${a.name}`}>
              <DynamicLink href={a.link}>
                <div>
                  <Image
                    src={a.logo.data.attributes.url}
                    alt={a.name}
                    height={a.logo.data.attributes.height}
                    width={a.logo.data.attributes.width}
                    sizes="33vw, (min-width: 768px) 17vw"
                  />
                </div>
              </DynamicLink>
            </div>
          ))}
        </div>
      </section>
      <section className="relative bg-white text-blue-500">
        <div className="mx-auto max-w-6xl pb-12 md:grid md:grid-cols-5 md:gap-12 md:pb-0">
          <div className="md:col-span-2">
            <Image
              src={expectations}
              alt="Bunk Beds"
              className="md:rounded-lg"
              sizes="100vw, (min-width: 768px) 33vw"
            />
          </div>
          <div className="flex h-full flex-col justify-center p-4 text-center md:col-span-3 md:p-0 md:text-left">
            <h2 className="mb-4 font-operetta text-3xl md:text-5xl">
              expectations of
              <br />
              referring agencies
            </h2>
            <div className="flex flex-col gap-4 font-avenir text-lg leading-6 md:pr-8">
              <p>
                <b>Referring Agencies</b> are vital in helping Beds for Kids execute our mission. We welcome
                organizations that work with children and families who are transitioning from homelessness or other
                situations of need, to permanent housing and are then without necessary furnishings to make their house
                a home.
              </p>
              <ul className="ml-[3px] flex flex-col gap-4 text-left md:gap-8">
                <li className="border-l-[3px] border-green-500 py-1 pl-8">
                  Generally, must be a 501(c)3 organization and/or a government agency
                </li>
                <li className="border-l-[3px] border-green-500 py-1 pl-8">
                  Required to have a designated contact that manages the referrals to Beds for Kids
                </li>
                <li className="mb-8 border-l-[3px] border-green-500 py-1 pl-8">
                  Required to participate in a yearly Beds for Kids information session with designated Beds for Kids
                  staff member(s).
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className={`absolute bottom-0 w-full`}>
          <BlueArch />
        </div>
      </section>
      <Scroll.Element name="agency-apply">
        <section className="bg-blue-500 pb-12 pt-4 text-center text-white">
          <div className="mx-auto max-w-3xl px-4">
            <h2 className="font-operetta text-5xl">agency referral application</h2>
            <h6 className="mt-8 font-avenir text-2xl font-medium">Think you might be a good fit for Beds for Kids?</h6>
            <p className="mt-8 font-avenir text-lg leading-6">
              Head over to the contact page and tell us a little bit about your agency! We are excited to hear from you!
            </p>
            <div className="mt-8 text-center">
              <Button link="/contact#form" bType="secondary-white">
                contact us now!
              </Button>
            </div>
          </div>
        </section>
      </Scroll.Element>
    </main>
  );
}

export function DynamicLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  if (href) {
    return (
      <A href={href} external>
        {children}
      </A>
    );
  }

  return <>{children}</>;
}
